/** "Summary" tab on the Contract Detail page.
 *
 * Pulls the executive synthesis from the Final Gate agent's output
 * (preferred — has cross-agent context) and falls back to the
 * Orchestrator Bootstrap narrative if Gate is missing. Adds risk,
 * verdict, confidence and a quick counts grid so the reviewer can
 * judge the contract at a glance without opening the per-agent tabs.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, BarChart3 } from 'lucide-react';
import { RichBootstrapBody } from '@/pages/jobs/stages/RichBootstrapBody';
import { ConfidenceBadge } from '@/pages/jobs/stages/ConfidenceBadge';
import { VERDICT_CLS } from '@/pages/jobs/stages/gateConstants';
import { getContractTypeSchema } from './contractTypeSchema';
import type { ContractDetail } from './types';
import type { AgentResult } from '@/types';

const asText = (v: unknown): string => (typeof v === 'string' ? v : '');
const asArr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' ? (v as Record<string, unknown>) : {};

function pickAgent(agents: AgentResult[], name: string): Record<string, unknown> {
  const row = agents.find((a) => a.agent_name === name);
  return obj(row?.output);
}

interface Props {
  contract: ContractDetail;
  agents: AgentResult[];
}

export function ContractSummaryView({ contract, agents }: Props) {
  const gate = pickAgent(agents, 'gate');
  const bootstrap = pickAgent(agents, 'orchestrator_bootstrap');
  const doc = pickAgent(agents, 'document');
  const parties = pickAgent(agents, 'parties');
  const clause = pickAgent(agents, 'clause');
  const compliance = pickAgent(agents, 'compliance');
  const signature = pickAgent(agents, 'signature');

  const verdict = asText(gate.pipeline_verdict) || 'PENDING';
  const verdictCls = VERDICT_CLS[verdict] ?? '';
  const summary = asText(gate.executive_summary) || asText(contract.contract_summary);
  const risk = obj(gate.overall_risk_assessment);
  const riskScore = risk.contract_risk_score;
  const riskCat = asText(risk.risk_category);

  const counts: { label: string; value: number }[] = [
    { label: 'Parties', value: asArr(parties.parties).length },
    { label: 'Clauses', value: asArr(clause.clauses).length },
    { label: 'Signatures', value: asArr(signature.signature_blocks).length },
    {
      label: 'Compliance issues',
      value:
        asArr(compliance.contractual_compliance_issues).length +
        asArr(obj(compliance.gdpr_analysis).gdpr_issues).length +
        asArr(compliance.missing_compliance_clauses).length +
        asArr(obj(compliance.jurisdiction_compliance).jurisdiction_issues).length,
    },
  ];

  // Type-aware highlight rows: the same contractTypeSchema that drives the
  // metadata card, so an NDA shows Discloser/Recipient and no value, a lease
  // shows Lessor/Lessee + Rent + term, etc. Columns never change — only which
  // rows appear and how the party/value fields are labelled.
  const schema = getContractTypeSchema(contract.contract_type);
  const formattedValue = contract.contract_value
    ? `${contract.currency} ${Number(contract.contract_value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
    : null;

  const glanceRows: { label: string; value: string }[] = [
    { label: schema.vendorLabel, value: contract.vendor_name || '—' },
    { label: schema.customerLabel, value: contract.customer_name || '—' },
  ];
  if (schema.showValue) {
    glanceRows.push({ label: schema.valueLabel, value: formattedValue ?? '—' });
  }
  if (schema.showTerm) {
    glanceRows.push({ label: 'Start date', value: contract.start_date || '—' });
    glanceRows.push({ label: 'End date', value: contract.end_date || '—' });
  }
  if (schema.showRenewal) {
    glanceRows.push({
      label: 'Auto-renewal',
      value: contract.auto_renewal
        ? `Yes${contract.notice_period_days ? ` — ${contract.notice_period_days}d notice` : ''}`
        : 'No',
    });
  }
  glanceRows.push({ label: 'Governing law', value: contract.governing_law || '—' });
  glanceRows.push({
    label: 'Document type',
    value: asText(doc.document_type) || contract.document_class || '—',
  });
  if (contract.overall_balance) {
    glanceRows.push({ label: 'Balance', value: contract.overall_balance });
  }

  return (
    <div className="space-y-3">
      <Card className="border-border">
        <CardContent className="pt-3 pb-3 px-4 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              Pipeline verdict
            </span>
            <Badge variant="outline" className={`text-xs ${verdictCls}`}>{verdict}</Badge>
            {riskCat && (
              <Badge variant="outline" className={`text-[10px] ${VERDICT_CLS[riskCat] ?? ''}`}>
                Risk: {riskCat}
              </Badge>
            )}
            {typeof riskScore === 'number' && (
              <span className="text-xs text-muted-foreground">{riskScore}/100</span>
            )}
          </div>
          {summary && (
            <p className="text-sm whitespace-pre-wrap">{summary}</p>
          )}
          {!summary && (
            <p className="text-sm text-muted-foreground italic flex items-center gap-2">
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              No executive summary yet — the Final Gate stage hasn't completed.
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Extracted at a glance
            <Badge variant="outline" className="ml-auto text-[10px]">{schema.label}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-3">
            {counts.map((c) => (
              <div key={c.label} className="rounded border p-3 text-center">
                <p className="text-2xl font-bold tabular-nums">{c.value}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{c.label}</p>
              </div>
            ))}
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs mt-3">
            {glanceRows.map((r) => (
              <div key={r.label} className="flex justify-between gap-3 border-b py-0.5">
                <dt className="text-muted-foreground">{r.label}</dt>
                <dd className="text-right break-words">{r.value}</dd>
              </div>
            ))}
          </dl>
          <div className="flex flex-wrap gap-2 mt-2">
            <ConfidenceBadge
              score={contract.confidence_doc}
              rationale={asText(doc.extraction_notes)}
              label="Doc"
            />
            <ConfidenceBadge
              score={contract.confidence_parties}
              rationale={asText(parties.extraction_notes)}
              label="Parties"
            />
            <ConfidenceBadge
              score={contract.confidence_clause}
              rationale={asText(clause.extraction_notes)}
              label="Clause"
            />
          </div>
        </CardContent>
      </Card>

      {(asText(bootstrap.narrative) || asText(bootstrap.executive_summary)) && (
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Final client report</CardTitle>
          </CardHeader>
          <CardContent>
            <RichBootstrapBody output={bootstrap} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
